import React, { createContext, useEffect, useState, useContext } from 'react';
import api from '../services/api';

const NotificationContext = createContext();

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let es;
    let shouldStop = false;
    let reconnectAttempt = 0;
    let reconnectTimer = null;
    let token = localStorage.getItem('token');
    if (!token) return;

    const base = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:5050';
    const connect = () => {
      if (shouldStop) return;
      const url = `${base}/api/notifications/stream?token=${encodeURIComponent(token)}`;
      try {
        es = new EventSource(url);
      } catch (e) {
        console.warn('SSE not supported', e);
        return;
      }
      es.onopen = () => {
        setConnected(true);
        reconnectAttempt = 0;
      };
      es.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          setNotifications(n => [payload, ...n]);
          setUnreadCount(c => c + 1);
        } catch (e) { console.warn('Invalid SSE payload', e); }
      };
      es.onerror = () => {
        setConnected(false);
        try { es.close(); } catch (e) {}
        // exponential backoff reconnect
        if (shouldStop) return;
        reconnectAttempt = Math.min(6, reconnectAttempt + 1);
        const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempt));
        reconnectTimer = setTimeout(() => connect(), delay);
      };
    };

    connect();
    // load initial notifications
    api.get('/api/notifications?include=new,booking,activity').then(r => {
      const list = Array.isArray(r.data.notifications) ? r.data.notifications : (Array.isArray(r.data) ? r.data : []);
      setNotifications(list);
      const unread = list.filter(n => n.unread).length;
      setUnreadCount(unread);
    }).catch(()=>{});

    return () => { shouldStop = true; if (reconnectTimer) clearTimeout(reconnectTimer); try { if (es) es.close(); } catch (e) {} };
  }, []);

  const markAsRead = async (id) => {
    try {
      await api.patch(`/api/notifications/${id}/mark-read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, unread: false } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const refresh = async () => {
    try {
      const r = await api.get('/api/notifications?include=new,booking,activity');
      const list = Array.isArray(r.data.notifications) ? r.data.notifications : (Array.isArray(r.data) ? r.data : []);
      setNotifications(list);
      setUnreadCount(list.filter(n => n.unread).length);
    } catch (e) {
      // ignore
    }
  };

  const value = { notifications, unreadCount, connected, markAsRead, refresh };
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
